import { assert } from '@varos/util-typescript';
import { ChatLoaderBaseConfig } from '../../../base';
import { CallRequestUpsertLoader } from './callRequestUpsertInterface';
import { CallRequestUpsertAggregate } from './callRequestUpsertModel';
import { useMemo } from 'react';
import { AssistantContext } from '@varos/assistant-react';
import { ThreadDetailAggregate } from '../../../thread';
import { MessageItemAggregate } from '../../../message';

export function createCallRequestUpsertLoader(
    config: ChatLoaderBaseConfig
): CallRequestUpsertLoader {
    const { repository } = config;
    return {
        useLoad(context, props): CallRequestUpsertAggregate {
            const integrationQuery = repository.platform.integration.useRetrieve(
                context,
                props.integration.id,
                {
                    suspense: true,
                }
            );
            assert(integrationQuery.status === 'success', 'expected suspense');

            const definitionQuery = repository.platform.integrationDefinition.useRetrieve(
                context,
                integrationQuery.data.definition_id,
                {
                    suspense: true,
                }
            );
            assert(definitionQuery.status === 'success', 'expected suspense');

            // Load messages for this thread
            const messagesQuery = repository.assistant.local.message.useFind(
                context as AssistantContext,
                { thread: props.thread.id, limit: 25 },
                {
                    suspense: true,
                    staleTime: 0,
                    refetchOnMount: true,
                    refetchOnWindowFocus: true,
                    refetchOnReconnect: true,
                }
            );
            assert(messagesQuery.status === 'success', 'expected suspense for messages');

            const thread = useMemo((): ThreadDetailAggregate => {
                return {
                    thread: props.thread,
                    messages: messagesQuery.data.map(
                        (msg): MessageItemAggregate => ({
                            definition: definitionQuery.data,
                            instance: integrationQuery.data,
                            message: msg,
                        })
                    ),
                    integration: integrationQuery.data,
                    definition: definitionQuery.data,
                };
            }, [messagesQuery.data, integrationQuery.data, definitionQuery.data]);

            return {
                integration: integrationQuery.data,
                definition: definitionQuery.data,
                thread,
            };
        },
    };
}
